import { format, parseISO } from "date-fns";
import { et, enGB } from "date-fns/locale";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  ticks,
  scaleWidth,
  type GanttScale,
  type GanttGranularity,
} from "@/domains/pm/utils/ganttScale";
import { GANTT_HEADER_HEIGHT, GANTT_LABEL_WIDTH } from "./ganttConstants";

/** Tick labels per granularity. The scale itself stays free of i18n; this is
 *  the only place date formatting happens on the axis. */
function tickLabel(iso: string, granularity: GanttGranularity, locale: Locale): string {
  const d = parseISO(iso);
  if (granularity === "day") return format(d, "d", { locale });
  if (granularity === "week") return format(d, "d MMM", { locale });
  return format(d, "MMM yyyy", { locale });
}

type Locale = typeof enGB;

/**
 * The date axis, and the view's controls.
 *
 * The corner cell was empty and is exactly the width of the label column, so
 * the controls live there rather than in a strip of their own above the chart
 * — one row instead of two, and the timeline starts higher up the page.
 */
export function GanttHeader({
  scale,
  labelWidth = GANTT_LABEL_WIDTH,
  controls,
  resizeHandle,
}: {
  scale: GanttScale;
  labelWidth?: number;
  /** Rendered in the corner cell: granularity, snapshot, history. */
  controls?: ReactNode;
  /** The label column's drag handle, on the corner cell's right edge. */
  resizeHandle?: ReactNode;
}) {
  const { language } = useLanguage();
  const locale = language === "et" ? et : enGB;
  const rows = ticks(scale, (iso, g) => tickLabel(iso, g, locale));

  return (
    <div
      data-gantt-header
      className="sticky top-0 z-20 flex border-b bg-background"
      style={{ width: labelWidth + scaleWidth(scale) }}
    >
      {/* Corner cell — sticky in BOTH axes so it stays over the label column. */}
      <div
        className="relative sticky left-0 z-10 flex shrink-0 items-center gap-1.5 border-r bg-background px-2"
        style={{ width: labelWidth, height: GANTT_HEADER_HEIGHT }}
      >
        {controls}
        {resizeHandle}
      </div>
      <div className="relative" style={{ width: scaleWidth(scale), height: GANTT_HEADER_HEIGHT }}>
        {rows.map((t) => (
          <div
            key={t.iso}
            className={cn(
              "absolute top-0 flex items-center justify-center border-r text-[10px] text-muted-foreground",
              scale.granularity === "day" && "px-0"
            )}
            style={{ left: t.x, width: t.width, height: GANTT_HEADER_HEIGHT }}
          >
            {t.label}
          </div>
        ))}
      </div>
    </div>
  );
}
